import type { FirstRunDecision } from "../../onboarding/firstRun.logic";

/**
 * Forge-only addition on top of the ported first-run decision in
 * `../../onboarding/firstRun.logic.ts`: a cloud Environment already known to
 * CMMD's own control-plane catalog is definitive evidence that Environment
 * setup already happened, independent of whether the client has connected to
 * it yet.
 *
 * `resolveFirstRunDecision` reads live project and thread counts out of the
 * client's environment store, which stays empty for a suspended Environment
 * until it wakes and reconnects. That empty state is indistinguishable, by
 * project/thread count alone, from a workspace that has genuinely never been
 * used, so a returning user whose only Environment happens to be suspended
 * was misread as first-run (CMMD-Center/forge#3626). Once redirected to
 * /welcome, the thread route, and the connect logic that would have woken
 * the Environment, never gets a chance to mount.
 *
 * This never widens what counts as "first run"; it only narrows a "wizard"
 * decision, either back to "app" when the cloud catalog proves the account
 * is not new, or to "pending" while the catalog has not settled yet.
 */

/**
 * A never-fetched catalog ("unsettled") must not read as "zero Environments":
 * `createCmmdForgeRuntimeListResource`'s initial snapshot, before any cache or
 * network response, is `{ isLoading: false, environments: [], fetchedAt: null
 * }`, the same shape as a genuinely empty account. Treating that as "no
 * Environment" would reopen exactly the bug this module fixes on the first
 * render of a browser with no local cache: `resolveFirstRunDecision` can
 * already be at "wizard" (every OTHER piece of evidence settled) before this
 * catalog's own `useEffect` has even fired once.
 *
 * A cached snapshot answers the "has-environment" branch immediately: the
 * resource seeds its initial state from `loadCachedRows()` with `fetchedAt`
 * already set, which is exactly the fast path a returning user with a
 * previously-cached catalog takes.
 */
export type CloudEnvironmentCatalogState = "empty" | "has-environment" | "unsettled";

export function classifyCloudEnvironmentCatalog(input: {
  readonly isLoading: boolean;
  readonly fetchedAt: string | null;
  readonly environmentCount: number;
}): CloudEnvironmentCatalogState {
  if (input.environmentCount > 0) return "has-environment";
  if (input.isLoading || input.fetchedAt === null) return "unsettled";
  return "empty";
}

export function applyCloudEnvironmentGate(input: {
  readonly nextDecision: FirstRunDecision;
  readonly persistCompletion: boolean;
  readonly cloudEnvironmentGateApplies: boolean;
  readonly cloudEnvironmentCatalogState: CloudEnvironmentCatalogState;
}): { readonly decision: FirstRunDecision; readonly persistCompletion: boolean } {
  if (!input.cloudEnvironmentGateApplies || input.nextDecision !== "wizard") {
    return { decision: input.nextDecision, persistCompletion: input.persistCompletion };
  }
  if (input.cloudEnvironmentCatalogState === "has-environment") {
    return { decision: "app", persistCompletion: false };
  }
  if (input.cloudEnvironmentCatalogState === "unsettled") {
    // "pending" is a no-op event in `transitionFirstRunGateState`: it holds
    // the gate's current state (whatever it already was) rather than
    // committing "wizard", so nothing latches before the catalog answers.
    return { decision: "pending", persistCompletion: false };
  }
  return { decision: input.nextDecision, persistCompletion: input.persistCompletion };
}
